/* eslint-disable no-console */
import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useOutsideClick, useSetState } from '@campaignhub/react-hooks'

import axios from 'axios'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

import {
  Box, Button, Checkbox, Tag, Text, Toggle,
} from '@campaignhub/suit-theme'

import { faFolderDownload, faSlidersH } from '@fortawesome/pro-light-svg-icons'
import { faCaretDown, faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { CLIENT_GENERATED_ASSET_TYPE_ID } from '@functions/assetType'
import { isFeatureEnabled } from '@functions/features'
import { itemPreviewSource } from '@functions/previewSource'
import { useAiGeneratedContentIds } from '@hooks/useBlobMetadata'
import useReduxAction from '@hooks/useReduxAction'

import FilePreviewOverlay from '@components/FilePreviewOverlay'

import custom from '@styles/custom.module.scss'
import ListItem from './components/ListItem'

const categories = [
  { key: 'resources', label: 'Resources' },
  { key: 'assets', label: 'Assets' },
  { key: 'aiGenerated', label: 'AI Generated' },
]

// Resource types and asset types are filtered separately; both sit under the same ids so they
// can't share a list. null means every type of that kind, which is also what a newly arrived
// type falls under — the options are derived from the data, so they grow as it loads.
const filterGroups = [
  { key: 'resources', label: 'Resources', allLabel: 'All Resources', stateKey: 'resourceTypeFilter' },
  { key: 'assets', label: 'Assets', allLabel: 'All Assets', stateKey: 'assetTypeFilter' },
]

const defaultState = {
  assetTypeFilter: null,
  previewItem: null,
  resourceTypeFilter: null,
  selectedKeys: [],
  showAiAssets: false,
  showFilter: false,
  zipCount: 0,
  zipLoading: false,
}

// Redux hands entities back in insertion order, so rows need an explicit one. Upload date ascending
// matches the Assets tab's default, with the name breaking ties.
const byUploadDate = (a, b) => (a.createdAt || '').localeCompare(b.createdAt || '')
  || (a.name || '').localeCompare(b.name || '')

const groupByService = items => items.reduce((groups, item) => {
  (groups[item.serviceName] = groups[item.serviceName] || []).push(item)
  return groups
}, {})

const typeOptions = (items) => {
  const counts = items.reduce((tally, item) => ({ ...tally, [item.typeLabel]: (tally[item.typeLabel] || 0) + 1 }), {})

  return Object.entries(counts)
    .map(([label, count]) => ({ count, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

const matchesTypeFilter = (filter, item) => !filter || filter.includes(item.typeLabel)

// Read the generated preview off the current file rather than trusting the DTO's previewUrl, whose
// fallback appends ?type=preview to a url that may already carry a SAS query string.
const previewRequestFor = content => content.file?.filePreviewRequests
  ?.find(request => request.imageType === 'Preview' && request.generatedAt && request.downloadUrl)

// A campaign can hold the same filename twice over — two jobs of one service, or a resource and an
// asset of the same shoot — and JSZip silently keeps only the last write to a path.
const zipPath = (item, taken) => {
  const folder = `${item.categoryLabel}/${item.serviceName}`
  const filename = item.originalName || item.name
  const extensionAt = filename.lastIndexOf('.')
  const base = extensionAt > 0 ? filename.slice(0, extensionAt) : filename
  const extension = extensionAt > 0 ? filename.slice(extensionAt) : ''

  let path = `${folder}/${filename}`
  let duplicate = 2
  while (taken.has(path)) {
    path = `${folder}/${base} (${duplicate})${extension}`
    duplicate += 1
  }

  taken.add(path)
  return path
}

const Related = (props) => {
  const { serviceJob } = props

  const [state, setState] = useSetState(defaultState)
  const {
    assetTypeFilter, resourceTypeFilter, selectedKeys, showAiAssets, showFilter, previewItem, zipCount, zipLoading,
  } = state

  const entities = useSelector(reduxState => reduxState.entities)
  const { assets, resources, serviceJobs } = entities

  const campaignId = serviceJob.campaign?.id

  // Siblings are already in redux (loadServiceJobV1 -> loadRelatedServiceJobs). Guard on
  // campaignId: matched against undefined, joblist jobs loaded without a campaign read as siblings.
  const relatedServiceJobIds = campaignId
    ? Object.values(serviceJobs)
      .filter(job => job.campaign?.id === campaignId && job.id !== serviceJob.id)
      .map(job => job.id)
      .sort((a, b) => a - b)
    : []

  const relatedServiceJobKey = relatedServiceJobIds.join(',')
  const campaignAssetsKey = `campaign-${campaignId}`

  // Only this tab needs the other jobs' assets, so the fetch rides its mount. histories is what
  // resolves previewUrl to the generated preview — without it AssetDto falls back to the legacy
  // asset stream, which a PDF can't render from.
  const { loadedForKeys, loading: loadingAssets, success } = useReduxAction('assets', 'loadAssets', {
    entityKey: campaignAssetsKey,
    serviceJobs: relatedServiceJobKey,
    includes: 'file,histories,file.filePreviewRequests',
  }, [relatedServiceJobKey], {
    shouldPerformFn: reducer => !!relatedServiceJobKey && !reducer.loadedForKeys.includes(campaignAssetsKey),
  })

  // success is this mount's own fetch outcome — undefined until it resolves. loadAssets records
  // the key as it dispatches, so an unrecorded key means the fetch hasn't started yet.
  const relatedAssetsFailed = success === false
  const loadingRelatedAssets = !!relatedServiceJobKey && !relatedAssetsFailed
    && (!loadedForKeys.includes(campaignAssetsKey) || loadingAssets)

  const itemBase = (content, sourceJob) => {
    // A generated preview request is proof enough on its own; previewGeneratedAt only has to vouch
    // for the DTO's own previewUrl, which may be a legacy asset-stream url.
    const previewRequest = previewRequestFor(content)
    const previewUrl = previewRequest?.downloadUrl || content.previewUrl

    return {
      createdAt: content.file?.createdAt || content.createdAt,
      fileTypeName: content.file?.fileType?.name,
      hasPreview: !!previewRequest || !!(previewUrl && content.file?.previewGeneratedAt),
      previewUrl,
      serviceName: sourceJob?.service?.name || 'Other Services',
    }
  }

  const relatedResources = Object.values(resources)
    .filter(resource => resource.campaignId === campaignId && resource.serviceJobId !== serviceJob.id)
    .map(resource => ({
      ...itemBase(resource, serviceJobs[resource.serviceJobId]),
      downloadUrl: resource.file?.downloadUrl || resource.resourceUrl,
      key: `resource:${resource.id}`,
      link: `/service-jobs/${resource.serviceJobId}`,
      name: resource.file?.displayName || resource.file?.originalName || resource.description,
      originalName: resource.file?.originalName,
      typeLabel: resource.resourceType?.description || 'Other',
    }))

  const relatedAssets = Object.values(assets)
    .filter(asset => relatedServiceJobIds.includes(asset.serviceJobId) && (asset.file || asset.copy || asset.directUrl))

  const { aiContentIds, loading: loadingAiFlags } = useAiGeneratedContentIds(relatedAssets.filter(asset => asset.file))

  // ClientGenerated is the AI Studio's saved output, so it carries the blob flag too — matching the
  // type as well classifies it without waiting on the HEAD, or if the HEAD fails.
  const isAiAsset = asset => asset.assetTypeId === CLIENT_GENERATED_ASSET_TYPE_ID || aiContentIds.has(asset.id)

  // A copy job's deliverable is its docx, which is generated onto its own file-bearing asset —
  // the template rows carry no file and show no row until the docx exists.
  const copyJobIds = new Set(relatedAssets.filter(asset => asset.copy).map(asset => asset.serviceJobId))

  const copyRows = [...copyJobIds]
    .map(jobId => relatedAssets
      .filter(asset => asset.serviceJobId === jobId && asset.file)
      .sort((a, b) => (b.file?.id || 0) - (a.file?.id || 0))[0])
    .filter(Boolean)

  const assetItem = asset => ({
    ...itemBase(asset, serviceJobs[asset.serviceJobId]),
    downloadUrl: asset.file ? asset.file.downloadUrl || asset.assetUrl : null,
    directUrl: asset.directUrl,
    isAiGenerated: isAiAsset(asset),
    key: `asset:${asset.id}`,
    link: `/service-jobs/${asset.serviceJobId}`,
    // Copy rows show the product name — the job's name — in place of the asset's file name.
    name: copyJobIds.has(asset.serviceJobId)
      ? serviceJobs[asset.serviceJobId]?.name || asset.file?.displayName || asset.file?.originalName || asset.description
      : asset.file?.displayName || asset.file?.originalName || asset.description,
    originalName: asset.file?.originalName,
    typeLabel: copyJobIds.has(asset.serviceJobId) ? 'Copy' : asset.assetType?.name || asset.file?.fileType?.name || 'Other',
  })

  const resourceItems = relatedResources
  const assetItems = [
    ...relatedAssets.filter(asset => !isAiAsset(asset) && !copyJobIds.has(asset.serviceJobId)).map(assetItem),
    ...copyRows.map(assetItem),
  ]
  const aiItems = relatedAssets.filter(isAiAsset).map(assetItem)

  // Options and their counts come off the unfiltered lists, so ticking one type doesn't renumber
  // the rest. Both asset sections share the asset-type list.
  const optionsByGroup = {
    resources: typeOptions(resourceItems),
    assets: typeOptions([...assetItems, ...aiItems]),
  }

  const itemsByCategory = {
    resources: showAiAssets ? [] : resourceItems.filter(item => matchesTypeFilter(resourceTypeFilter, item)),
    assets: showAiAssets ? [] : assetItems.filter(item => matchesTypeFilter(assetTypeFilter, item)),
    aiGenerated: aiItems.filter(item => matchesTypeFilter(assetTypeFilter, item)),
  }

  // The AI marker is blob metadata, so rows start under Assets and move to AI Generated once the
  // HEADs land. Keys don't change, so a selection survives the move.
  const settling = loadingRelatedAssets || loadingAiFlags

  const visibleItems = categories
    .flatMap(category => itemsByCategory[category.key].map(item => ({ ...item, categoryLabel: category.label })))

  // Same order the rows render in (category -> service -> upload date) so next/back follows the page.
  const orderedVisibleItems = categories.flatMap(category => {
    const groupedItems = groupByService(itemsByCategory[category.key])
    return Object.keys(groupedItems).sort()
      .flatMap(serviceName => [...groupedItems[serviceName]].sort(byUploadDate))
  })

  const toPreviewItem = (item) => {
    const source = itemPreviewSource(item)
    if (!source) {
      return null
    }

    return {
      key: item.key,
      name: item.name,
      downloadName: item.originalName || item.name,
      previewUrl: source.url,
      videoUrl: source.videoUrl,
      downloadUrl: item.downloadUrl,
      reducedResolution: source.reduced,
    }
  }

  const previewItems = orderedVisibleItems.map(toPreviewItem).filter(Boolean)

  const selectedItems = visibleItems.filter(item => selectedKeys.includes(item.key))
  const selectableItems = visibleItems.filter(item => item.downloadUrl)
  const allSelected = !!selectableItems.length && selectableItems.every(item => selectedKeys.includes(item.key))

  const toggleSelected = (key) => {
    setState({
      selectedKeys: selectedKeys.includes(key) ? selectedKeys.filter(selected => selected !== key) : [...selectedKeys, key],
    })
  }

  // Select All covers what the filter leaves on screen and skips rows with nothing to download,
  // mirroring the Resources tab.
  const toggleSelectAll = () => {
    const selectableKeys = selectableItems.map(item => item.key)

    setState({
      selectedKeys: allSelected
        ? selectedKeys.filter(key => !selectableKeys.includes(key))
        : [...new Set([...selectedKeys, ...selectableKeys])],
    })
  }

  const filterFor = group => state[group.stateKey]
  const allTypesSelected = group => !filterFor(group) || filterFor(group).length === optionsByGroup[group.key].length
  const typeSelected = (group, label) => !filterFor(group) || filterFor(group).includes(label)

  // Selecting every type collapses back to null, so a type that loads later stays included.
  const toggleTypeFilter = (group, label) => {
    const options = optionsByGroup[group.key]
    const selected = filterFor(group) || options.map(option => option.label)
    const next = selected.includes(label) ? selected.filter(x => x !== label) : [...selected, label]

    setState({ [group.stateKey]: next.length === options.length ? null : next })
  }

  const toggleAllTypes = group => setState({ [group.stateKey]: allTypesSelected(group) ? [] : null })

  const contentEl = useRef()
  const [isClickedOutside, setIsClickedOutside] = useOutsideClick(contentEl, { enabled: showFilter })

  useEffect(() => {
    if (isClickedOutside && showFilter) {
      setState({ showFilter: false })
      setIsClickedOutside(false)
    }
  }, [isClickedOutside])

  const countRef = useRef()
  const totalRef = useRef()

  const downloadSelected = (items) => {
    const zip = new JSZip()
    const takenPaths = new Set()
    let downloaded = 0
    let failed = 0
    setState({ zipLoading: true, zipCount: items.length })

    const downloads = items.map(item => axios.get(item.downloadUrl, { responseType: 'blob' })
      .then((response) => {
        zip.file(zipPath(item, takenPaths), response.data)
        downloaded += 1
        if (countRef.current) {
          countRef.current.textContent = downloaded
        }
      })
      .catch((error) => {
        failed += 1
        console.error(error)
        console.error(item)
      }))

    Promise.all(downloads)
      // A file that failed is otherwise just absent from the zip, with nothing on screen to say so.
      .then(() => {
        if (failed) {
          toast.warning(`${failed} of ${items.length} file${items.length === 1 ? '' : 's'} could not be downloaded`)
        }
      })
      .then(() => zip.generateAsync({ type: 'blob' }, (metadata) => {
        if (countRef.current && totalRef.current) {
          countRef.current.textContent = metadata.percent.toFixed(0)
          totalRef.current.textContent = '% Processing Zip'
        }
      }).then((blob) => {
        saveAs(blob, `${serviceJob.campaign.name}.zip`)
        setState({ zipLoading: false })
      }))
      .catch((error) => {
        console.error(error)
        setState({ zipLoading: false })
      })
  }

  const hasRelatedContent = !!(resourceItems.length || assetItems.length || aiItems.length)
  const hasVisibleContent = !!visibleItems.length

  return (
    <Box flexDirection="column" fontSize="small" className={custom.scroll}>
      <Box
        alignItems="center"
        borderBottom="1px solid"
        borderColor="lineColor"
        flexDirection="row"
        padding="large"
        className={custom.root}
        ref={contentEl}
        style={{ justifyContent: 'space-between' }}
      >
        <Box width="auto" flexDirection="row" color="blue" onClick={() => setState({ showFilter: !showFilter })} style={{ cursor: 'pointer' }}>
          <FontAwesomeIcon icon={faSlidersH} />
          <Text paddingX="medium" color="blue">Filter</Text>
          <FontAwesomeIcon icon={faCaretDown} />
        </Box>
        <Box alignItems="center" flexShrink="0" width="auto" style={{ marginRight: 6 }}>
          <Button
            buttonStyle="secondary"
            disabled={!selectedItems.length || zipLoading}
            marginRight="medium"
            onClick={() => downloadSelected(selectedItems)}
            size="small"
            title="Download"
            width="auto"
          >
            {zipLoading && (
              <>
                <Text ref={countRef} marginRight="small"> 0 </Text>
                <Text ref={totalRef} marginRight="large"> / {zipCount} </Text>
              </>
            )}
            <FontAwesomeIcon icon={zipLoading ? faSpinner : faFolderDownload} className={zipLoading ? 'fa-spin' : ''} />
          </Button>
          <Tag boxProps={{ width: 'fit-content', fontSize: 'xsmall', marginRight: 'large' }}>{selectedItems.length} Selected</Tag>
          <Checkbox checked={allSelected} onClick={() => toggleSelectAll()} />
        </Box>
        {showFilter && (
          <Box className={custom.popup} width="300px">
            <Box flexDirection="column" maxHeight="280px" overflowY="auto">
              {filterGroups.filter(group => optionsByGroup[group.key].length).map(group => (
                <Box flexDirection="column" key={group.key} paddingBottom="medium">
                  <Text color="bodyFontLightColor" fontSize="xsmall" paddingBottom="medium">
                    {group.label}
                  </Text>
                  <Box flexDirection="row" paddingBottom="medium" alignItems="center">
                    <input
                      type="checkbox"
                      className={custom.checkbox}
                      checked={allTypesSelected(group)}
                      onChange={() => toggleAllTypes(group)}
                    />
                    <Text fontSize="small" marginLeft="medium">{group.allLabel}</Text>
                  </Box>
                  {optionsByGroup[group.key].map(option => (
                    <Box flexDirection="row" key={option.label} paddingBottom="medium" paddingLeft="medium" alignItems="center">
                      <input
                        type="checkbox"
                        className={custom.checkbox}
                        checked={typeSelected(group, option.label)}
                        onChange={() => toggleTypeFilter(group, option.label)}
                      />
                      <Text fontSize="small" marginLeft="medium">
                        {option.label} ({option.count})
                      </Text>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
            {isFeatureEnabled('ai-retouching') && (
              <Box
                borderTop="1px solid"
                borderColor="lineColor"
                marginTop="medium"
                paddingTop="medium"
              >
                <Toggle
                  onClick={() => setState({ showAiAssets: !showAiAssets })}
                  toggleOffColor="bodyFontColor"
                  toggleOffText={`Show AI Assets Only (${aiItems.length})`}
                  toggleOn={showAiAssets}
                  toggleOnText={`Show AI Assets Only (${aiItems.length})`}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box flexDirection="column" padding="large" maxHeight="586px" overflowY="auto">
        {relatedAssetsFailed && (
          <Text color="red" fontSize="small" fontWeight="600" marginBottom="large">
            Couldn&apos;t load this campaign&apos;s other assets. Reopen this tab to try again.
          </Text>
        )}

        {categories.map((category) => {
          const groupedItems = groupByService(itemsByCategory[category.key])

          if (!Object.keys(groupedItems).length) return null

          return (
            <Box flexDirection="column" key={category.key} marginBottom="large">
              <Text fontSize="small" fontWeight={600} marginBottom="large">
                {category.label}
              </Text>
              {Object.keys(groupedItems).sort().map(serviceName => (
                <Box flexDirection="column" key={serviceName}>
                  <Text color="bodyFontLightColor" fontSize="xsmall" marginBottom="medium">
                    {serviceName}
                  </Text>
                  {groupedItems[serviceName].sort(byUploadDate).map(item => (
                    <ListItem
                      callbacks={{
                        togglePreview: () => setState({ previewItem: toPreviewItem(item) }),
                        toggleSelected: () => toggleSelected(item.key),
                      }}
                      item={item}
                      key={item.key}
                      selected={selectedKeys.includes(item.key)}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          )
        })}

        {!hasVisibleContent && !settling && !relatedAssetsFailed && (
          <Box
            alignItems="center"
            color="bodyFontColor"
            display="grid"
            height={[150, 250]}
            justifyContent="center"
          >
            <Text fontSize="small" fontWeight="600">
              {hasRelatedContent
                ? 'No related items match this filter.'
                : 'No related resources or assets on this campaign.'}
            </Text>
          </Box>
        )}
      </Box>
      {previewItem && (
        <FilePreviewOverlay
          item={previewItem}
          items={previewItems}
          onClose={() => setState({ previewItem: null })}
          onNavigate={(item) => setState({ previewItem: item })}
        />
      )}
    </Box>
  )
}

Related.propTypes = {
  serviceJob: PropTypes.object.isRequired,
}

export default Related
